import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ColumnConfigEntrySchema,
  TASK_PAGE_ICON_KEYS,
  TASK_PAGE_NAV_GROUPS,
  TaskPageDefaultFiltersSchema,
} from "@erp/shared-types";
import {
  PageSectionIdWriteSchema,
  SidebarSortOrderSchema,
} from "./pageSections.contract";

/**
 * Org-configurable "Task Pages" — sidebar-mountable task-queue pages showing
 * one or more task types (one tab each), with per-page user-group audiences
 * on top of RBAC.
 */

// Subset of the tasks-queue URL search params a page may seed its view with.
// The URL always wins once the user changes a filter. Defined once in
// @erp/shared-types and shared with the backend TaskPage model.
export { TaskPageDefaultFiltersSchema };

// Admin default queue columns (same shape as saved per-user column configs).
// Null = the built-in task-page column set. Column ids must be unique.
export const TaskPageDefaultColumnsSchema = z
  .array(ColumnConfigEntrySchema)
  .max(50)
  .refine(
    (columns) => new Set(columns.map((c) => c.id)).size === columns.length,
    "Column ids must be unique",
  );

/** Most task types one page may show (one tab each). */
export const MAX_TASK_PAGE_TASK_TYPES = 20;

// One task type a page shows, as its own tab, in tab order.
export const TaskPageTaskTypeResponseSchema = z.object({
  taskTypeId: z.uuid(),
  // Denormalized from the task type (the tab's `tab=<key>` search value).
  key: z.string(),
  name: z.string(),
  // Tab label; null renders `name`.
  label: z.string().nullable(),
  // This tab's admin default columns; null = the built-in task-page set.
  defaultColumns: z.array(ColumnConfigEntrySchema).nullable(),
});

// One task type in a create/update `taskTypes` list.
export const TaskPageTaskTypeWriteSchema = z.object({
  taskTypeId: z.uuid(),
  label: z.string().trim().max(255).nullable().optional(),
  defaultColumns: TaskPageDefaultColumnsSchema.nullable().optional(),
});

// Ordered, non-empty, unique by task type. Replaces the page's whole list.
export const TaskPageTaskTypesWriteSchema = z
  .array(TaskPageTaskTypeWriteSchema)
  .min(1)
  .max(MAX_TASK_PAGE_TASK_TYPES)
  .refine(
    (entries) =>
      new Set(entries.map((e) => e.taskTypeId)).size === entries.length,
    "Task types must be unique",
  );

// --- Response schema ---

export const TaskPageResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  // Lenient on output (plain string, not the icon-key enum) so removing a key
  // from the fixed set can never brick reads; the frontend renders a default
  // icon for unknown keys. Writes validate strictly.
  icon: z.string().nullable(),
  // The page's primary (first) task type; see `taskTypes` for the full list.
  taskTypeId: z.uuid(),
  // Denormalized from the page's task type for sidebar/queue rendering.
  taskTypeKey: z.string(),
  taskTypeName: z.string(),
  // Lenient on output for the same reason as `icon`: a renamed sidebar group
  // leaves stale labels behind; the frontend folds unknown labels into the
  // generic "Pages" section. Writes validate against TASK_PAGE_NAV_GROUPS.
  navGroup: z.string().nullable(),
  // Org-created sidebar section (pageSections.*) the page attaches to. When
  // set it wins over `navGroup`; the frontend folds an unknown id (archived
  // section) into the generic "Pages" section like an unmatched navGroup.
  pageSectionId: z.uuid().nullable(),
  sortOrder: z.number().int(),
  defaultFilters: TaskPageDefaultFiltersSchema.nullable(),
  // The primary type's default columns (mirrors taskTypes[0].defaultColumns).
  defaultColumns: z.array(ColumnConfigEntrySchema).nullable(),
  // Every task type the page shows, in tab order. Always at least one entry;
  // the first is `taskTypeId`. More than one renders a tab strip.
  taskTypes: z.array(TaskPageTaskTypeResponseSchema),
  // False: every user sees `defaultColumns`; saved per-user configs are
  // ignored and the queue hides its columns menu.
  allowUserColumnCustomization: z.boolean(),
  // User groups granted visibility. Empty = visible to every TaskPage reader.
  audienceUserGroupIds: z.array(z.uuid()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// --- Input schemas ---

const TaskPageWriteShape = {
  name: z.string().trim().min(1).max(255),
  description: z.string().max(10000).nullable().optional(),
  icon: z.enum(TASK_PAGE_ICON_KEYS).nullable().optional(),
  taskTypeId: z.uuid(),
  // Must be one of the shared sidebar group labels; null = generic "Pages"
  // section. See TASK_PAGE_NAV_GROUPS (kept in sync with route staticData).
  navGroup: z.enum(TASK_PAGE_NAV_GROUPS).nullable().optional(),
  pageSectionId: PageSectionIdWriteSchema,
  sortOrder: SidebarSortOrderSchema.optional(),
  defaultFilters: TaskPageDefaultFiltersSchema.nullable().optional(),
  defaultColumns: TaskPageDefaultColumnsSchema.nullable().optional(),
  allowUserColumnCustomization: z.boolean().optional(),
  audienceUserGroupIds: z.array(z.uuid()).max(100).optional(),
  // When present, REPLACES the page's ordered task types, and its first entry
  // overrides `taskTypeId` / `defaultColumns` on the page. Absent: a write of
  // `taskTypeId` / `defaultColumns` keeps meaning a single-type page.
  taskTypes: TaskPageTaskTypesWriteSchema.optional(),
} as const;

export const CreateTaskPageInputSchema = z.object(TaskPageWriteShape);

// All fields optional on update; `audienceUserGroupIds` (when present)
// REPLACES the audience set atomically.
export const UpdateTaskPageInputSchema = z.object({
  id: z.uuid(),
  ...TaskPageWriteShape,
  name: TaskPageWriteShape.name.optional(),
  taskTypeId: z.uuid().optional(),
});

export const ListTaskPagesInputSchema = z.object({
  // 'visible' (default): pages the CURRENT user may see (RBAC read +
  // audience filter) — powers the sidebar. 'manage': every page in the org,
  // regardless of audience — powers the admin card; requires any of Create/
  // Update/Delete on TaskPage.
  view: z.enum(["visible", "manage"]).optional(),
});

const TaskPageIdInputSchema = z.object({ id: z.uuid() });

const ArchiveTaskPageOutputSchema = z.object({ success: z.boolean() });

// One actor's share of a page's open tasks (sidebar hover breakdown).
// `actorId` is null only for the synthetic "unassigned" row.
export const TaskPageActorCountSchema = z.object({
  actorType: z.enum(["user", "agent", "unassigned"]),
  actorId: z.string().nullable(),
  name: z.string(),
  count: z.number().int().nonnegative(),
});

// Sidebar badge data for one task page: OPEN tasks (pending / in_progress /
// needs_input) of the page's task type, org-wide. `mine` is the caller's own
// share (the badge number); `total` + `byActor` (descending) feed the hover
// breakdown. Counts are org-wide — not scoped to the caller's task visibility
// — because they are aggregates, not task contents, and a per-actor breakdown
// that only ever showed the caller would be useless.
export const TaskPageCountSchema = z.object({
  mine: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  byActor: z.array(TaskPageActorCountSchema),
});

// Keyed by task page id, for the CURRENT user's visible pages only.
export const TaskPageCountsOutputSchema = z.object({
  counts: z.record(z.string(), TaskPageCountSchema),
});

// --- Contracts ---

export const taskPagesContracts = {
  list: oc
    .input(ListTaskPagesInputSchema)
    .output(z.array(TaskPageResponseSchema))
    .route({
      summary: "List task pages",
      description:
        "Returns task queue pages. Default view is pages visible to the current user for the sidebar; 'manage' returns every page in the organization for admins.",
      method: "GET",
    }),

  get: oc.input(TaskPageIdInputSchema).output(TaskPageResponseSchema).route({
    summary: "Get task page",
    description:
      "Returns one task queue page with its task types, sidebar placement, default filters and columns, and audience user groups.",
    method: "GET",
  }),

  counts: oc.output(TaskPageCountsOutputSchema).route({
    summary: "Count open tasks per task page",
    description:
      "Returns open task counts for each task page the user can see: the user's own count, the org-wide total, and a breakdown by assignee.",
    method: "GET",
  }),

  create: oc
    .input(CreateTaskPageInputSchema)
    .output(TaskPageResponseSchema)
    .route({
      summary: "Create task page",
      description:
        "Creates a sidebar task queue page showing one or more task types, with optional icon, sidebar section, default filters and columns, and audience user groups.",
      method: "POST",
    }),

  update: oc
    .input(UpdateTaskPageInputSchema)
    .output(TaskPageResponseSchema)
    .route({
      summary: "Update task page",
      description:
        "Updates a task queue page's name, task types, sidebar placement, default filters or columns; a supplied task type list or audience user group list replaces the existing one.",
      method: "PUT",
    }),

  archive: oc
    .input(TaskPageIdInputSchema)
    .output(ArchiveTaskPageOutputSchema)
    .route({
      summary: "Archive task page",
      description:
        "Removes a task queue page from the sidebar for everyone; its tasks are not affected.",
      method: "DELETE",
    }),
} as const;

// --- Type exports ---

export type TaskPageTaskTypeResponse = z.infer<
  typeof TaskPageTaskTypeResponseSchema
>;
export type TaskPageTaskTypeWrite = z.infer<typeof TaskPageTaskTypeWriteSchema>;
export type TaskPageResponse = z.infer<typeof TaskPageResponseSchema>;
export type TaskPageActorCount = z.infer<typeof TaskPageActorCountSchema>;
export type TaskPageCount = z.infer<typeof TaskPageCountSchema>;
export type TaskPageCountsOutput = z.infer<typeof TaskPageCountsOutputSchema>;
export type { TaskPageDefaultFilters } from "@erp/shared-types";
export type CreateTaskPageInput = z.infer<typeof CreateTaskPageInputSchema>;
export type UpdateTaskPageInput = z.infer<typeof UpdateTaskPageInputSchema>;
export type ListTaskPagesInput = z.infer<typeof ListTaskPagesInputSchema>;
